/**
 * ProviderCard - Square card for displaying content providers in collection rails
 *
 * Used in PROVIDER template collections. Displays a square logo with the provider's
 * display name below. Clicking navigates to the provider's public profile page.
 */

import type { ReactElement } from "react";
import type { ApiCollectionProviderItem } from "@lumi/data";
import type { Variants } from "framer-motion";
import { BadgeCheck } from "lucide-react";
import { useCascadeFadeIn } from "../../hooks";
import { pageTransitions } from "../../transitions/pageTransitions";

interface ProviderCardProps {
  readonly provider: ApiCollectionProviderItem["provider"];
  readonly index?: number;
  readonly className?: string;
  readonly transitionTo: (path: string, variant?: Variants) => void;
}

export function ProviderCard({ provider, index = 0, className = "", transitionTo }: ProviderCardProps): ReactElement {
  const { ref, isVisible } = useCascadeFadeIn({ index });

  // Use square logo if available, fall back to regular logo
  const imageUrl = provider.logoSqUrl ?? provider.logoUrl;

  const handleClick = () => {
    transitionTo(`/home/providers/${provider.slug}`, pageTransitions.fullScreenFade);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div //NOSONAR
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`View ${provider.displayName}'s profile`}
      className={`
        group flex-shrink-0 self-start cursor-pointer
        w-[calc(100vw-2rem)] sm:w-[300px] snap-center
        transition-all duration-500 ease-out
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
        ${className}
      `}
    >
      <div className="card-base card-hover-horizontal">
        {/* Square image container */}
        <div className="relative aspect-square overflow-hidden rounded-lg bg-neutral-900 ring-1 ring-neutral-800/50">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={provider.displayName}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            // Fallback: show first letter of display name
            <div className="flex items-center justify-center h-full bg-gradient-to-br from-neutral-800 to-neutral-900">
              <span className="text-4xl font-bold text-neutral-500">
                {provider.displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          {/* Verified badge */}
          {provider.verified && (
            <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-full p-1.5">
              <BadgeCheck className="w-4 h-4 text-lumi-accent" />
            </div>
          )}

          {/* Subtle gradient overlay on hover */}
          <div className="card-gradient-light" />
          <div className="card-gradient-dark" />
        </div>
      </div>
    </div>
  );
}
